<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useOrderEditStore } from '@/stores/orderEdit';
import { editErrorText, fillText, useEditTexts } from '@/composables/useEditTexts';
import { formatIsoDate } from '@/services/orderEditRules';
import EditContact from './edit-contact.vue';
import EditOutcome from './edit-outcome.vue';
import EditSection from './edit-section.vue';
import EditSummary from './edit-summary.vue';

// EAUG-77 §9.1: « Votre commande », opened from the signed edit link
// (?mode=edit&token=…). booking.vue renders it instead of the wizard, so the
// wizard's stores and the visitor's own cart (booking_* keys) stay untouched.
const { token, paidId = null, pollIntervalMs, pollTimeoutMs } = defineProps<{
  token: string;
  /** `&paid=<id>`: back from Stripe's success URL. */
  paidId?: number | null;
  /** Test seams; the page uses the spec's 4 s and 60 s. */
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
}>();

const store = useOrderEditStore();
const t = useEditTexts();

onMounted(() => {
  // load() records the credential before its first await: the poll can start right after.
  void store.load({ token });
  if (paidId !== null) store.startPaidPolling(paidId, { intervalMs: pollIntervalMs, timeoutMs: pollTimeoutMs });
});

const header = computed(() => {
  const v = store.view;
  if (!v) return '';
  return [
    fillText(t.edit_header_date, { date: formatIsoDate(v.date) }),
    v.roomLabel,
    fillText(t.edit_header_deadline, { deadline: v.deadlineLabel }),
  ]
    .filter((part) => part !== '')
    .join(' · ');
});

const intro = computed(() => {
  const name = store.view?.firstName.trim() ?? '';
  return name ? fillText(t.edit_intro, { firstName: name }) : t.edit_intro_anonymous;
});

// Back from Stripe, the payment's outcome is the page: a read refused since
// (flag turned off, deadline passed meanwhile) must not hide it.
const showLoadError = computed(() => store.loadState === 'error' && paidId === null);
// No stepper while a submit runs, once redirected, or after the deadline.
const frozen = computed(() => store.deadlinePassed || store.submitState !== 'idle');
</script>

<template>
  <div class="bpa-front-tabs--panel-body __bpa-is-active order-edit" data-testid="order-edit-page">
    <div class="bpa-front-default-card">
      <div class="bpa-front-dc--body">
        <div class="bpa-front-module-container bpa-front-module--service">
          <edit-outcome />
          <p v-if="store.loadState === 'loading'" class="order-edit__loading" data-testid="order-edit-loading">
            {{ t.edit_loading }}
          </p>
          <div
            v-if="showLoadError"
            class="order-edit__error"
            role="alert"
            data-testid="order-edit-error"
            :data-error-code="store.loadError ?? ''"
          >
            <p>{{ editErrorText(t, store.loadError) }}</p>
            <edit-contact />
          </div>
          <template v-if="store.loadState === 'ready' && store.view">
            <div class="bpa-front-module-heading" data-testid="order-edit-header">{{ header }}</div>
            <p class="order-edit__intro" data-testid="order-edit-intro">{{ intro }}</p>
            <edit-section section="menus" :frozen="frozen" />
            <edit-section section="drinks" :frozen="frozen" />
            <edit-section section="options" :frozen="frozen" />
            <edit-summary />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.order-edit__loading {
  margin: 24px 0;
}

.order-edit__error {
  margin: 12px 0;
  color: #b00020;
}

.order-edit__intro {
  margin: 8px 0 16px;
}
</style>
